import React, { useState } from 'react';
import { Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QueryQuote } from '@tassomutuo-backoffice/types';
import {
  CtaButton,
  PageContentLayout,
  ConfirmActionModal,
} from '@tassomutuo-backoffice/ui';
import QuoteModalForm from './components/QuoteModalForm';
import {
  createQuoteQuery,
  deleteQuoteQuery,
  getBanks,
  getPurposes,
  getQuoteQueries,
  getQuoteQueriesInProd,
  publishQueryCacheInProd,
} from '../../../services/api/quote/quote';
import QuoteQueriesTable from './components/QuoteQueriesTable';
import configEnv from '../../../config/config';

const QuotesPage = () => {
  const queryClient = useQueryClient();

  const [showModalForm, setShowModalForm] = useState(false);
  const [queryToDelete, setQueryToDelete] = useState<QueryQuote | null>(null);
  const [queryToPushInProd, setQueryToPushInProd] = useState<QueryQuote | null>(
    null
  );
  const {
    isPending: banksIsPending,
    data: banks = [],
    error: banksError,
  } = useQuery({
    queryKey: ['Banks'],
    queryFn: getBanks,
  });

  const {
    isPending: purposesIsPending,
    data: purposesLabelsValues = [],
    error: purposesError,
  } = useQuery({
    queryKey: ['Purposes'],
    queryFn: getPurposes,
  });

  const {
    isPending: quoteQueriesIsPending,
    data: quoteQueries = [],
    error: quoteQueriesError,
  } = useQuery({
    queryKey: ['QuoteQueries'],
    queryFn: getQuoteQueries,
  });

  const { data: prodQuoteQueries = [] } = useQuery({
    queryKey: ['QuoteQueriesProd'],
    queryFn: getQuoteQueriesInProd,
  });

  const createNewQuoteQuery = useMutation({
    mutationFn: createQuoteQuery,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['QuoteQueries'],
      });

      toast.success('Preventivo creato!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const deleteQuoteQueryMutation = useMutation({
    mutationFn: deleteQuoteQuery,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['QuoteQueries'],
      });

      toast.success('Preventivo eliminato!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const pushQueryCacheToProdMutation = useMutation({
    mutationFn: publishQueryCacheInProd,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ['QuoteQueries'],
      });
      queryClient.invalidateQueries({
        queryKey: ['QuoteQueriesProd'],
      });
      toast.success('Preventivi pubblicati in produzione');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const onDeleteClick = (query: QueryQuote) => {
    setQueryToDelete(query);
  };

  const onConfirmDeleteQuery = async () => {
    if (queryToDelete) {
      return deleteQuoteQueryMutation.mutateAsync(queryToDelete?.id);
    }
  };

  const onPushInProdClick = (query: QueryQuote) => {
    setQueryToPushInProd(query);
  };

  const onConfirmPushInProd = async () => {
    if (queryToPushInProd) {
      await pushQueryCacheToProdMutation.mutateAsync(queryToPushInProd.id);
    }
  };

  return (
    <>
      <PageContentLayout
        title="Gestione Preventivi"
        isLoading={banksIsPending || purposesIsPending || quoteQueriesIsPending}
        error={banksError || purposesError || quoteQueriesError}
      >
        {configEnv.isStage && (
          <Box display="flex" justifyContent="flex-end" px={4}>
            <CtaButton
              variant="contained"
              onClick={() => setShowModalForm(true)}
            >
              <AddIcon /> Aggiungi
            </CtaButton>
          </Box>
        )}
        <QuoteQueriesTable
          prodQuoteQueries={prodQuoteQueries}
          quoteQueries={quoteQueries}
          onDeleteClick={onDeleteClick}
          onPushInProdClick={onPushInProdClick}
        />
      </PageContentLayout>
      <QuoteModalForm
        open={showModalForm}
        handleClose={() => setShowModalForm(false)}
        banks={banks}
        purposesLabelsValues={purposesLabelsValues}
        onSubmitQuoteForm={createNewQuoteQuery.mutateAsync}
      />
      <ConfirmActionModal
        open={!!queryToDelete}
        title="Elimina Preventivo"
        description={`Sei sicuro di voler eliminare il preventivo ${queryToDelete?.id}? Tutti i dati a esso collegati andranno persi.`}
        onConfirm={onConfirmDeleteQuery}
        handleClose={() => setQueryToDelete(null)}
      />
      {queryToPushInProd && (
        <ConfirmActionModal
          open={!!queryToPushInProd}
          handleClose={() => setQueryToPushInProd(null)}
          title="Pubblica in produzione"
          description={`Sei sicuro di voler pubblicare il preventivo ${queryToPushInProd.id} in produzione?`}
          onConfirm={onConfirmPushInProd}
        />
      )}
    </>
  );
};

export default QuotesPage;
